<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import operationManagerService from "~/core/services/operationManager.service";
import {
  NotificationKey,
  showNotificationError,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import { DtwinOperationStatus } from "~/modules/dtwin/models/DtwinOperationStatus.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcEdit from "~icons/lxc/edit";
import ILxcStopCircle from "~icons/lxc/stop-circle";

const props = defineProps<{
  organizationCode: number;
  dtwin: Dtwin;
  dtwinModel: DtwinModel;
  dtwinOperationModel: OperationModel;
  operation: DtwinOperation;
}>();

const emit = defineEmits(["change", "edit"]);

const { t } = useI18n();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { can } = useAcl();

const disableUpdate = computed(
  () =>
    props.operation.status !== DtwinOperationStatus.SCHEDULED &&
    props.operation.status !== DtwinOperationStatus.PENDING &&
    props.operation.status !== DtwinOperationStatus.QUEUED,
);

const canUpdateOperations = computed(() => {
  switch (props.operation.type) {
    case OperationModelType.FIRM_UPDATE:
      return can([
        ACLRoles.CAN_MANAGE_FIRMWARE_OPERATION,
        ACLRoles.CAN_READ_FIRMWARE,
      ]);
    case OperationModelType.CERT_RENEW:
      return can(ACLRoles.CAN_MANAGE_CERTIFICATE_OPERATION);
    case OperationModelType.CONFIG:
      return can(ACLRoles.CAN_MANAGE_CONFIGURATION_OPERATION);
    default:
      return false;
  }
});

const [confirmCancelOperationDialogVisible, toggleConfirmCancel] = useToggle();

const isCancelling: Ref<boolean> = ref(false);

function edit() {
  emit("edit", props.operation);
}

async function cancel() {
  if (currentOrganization.value?.code) {
    toggleConfirmCancel(false);
    isCancelling.value = true;
    const response = await operationManagerService.cancel(
      currentOrganization.value?.code,
      props.operation.uid,
    );
    isCancelling.value = false;
    if (LxcError.check(response)) {
      showNotificationError(t(NotificationKey.error));
    } else {
      showNotificationSuccess(t(NotificationKey.success));
      emit("change");
    }
  }
}
</script>

<template>
  <lxc-loader v-if="isCancelling" />
  <div v-else-if="canUpdateOperations" class="flex">
    <lxc-button
      :disabled="disableUpdate"
      html-type="button"
      type="borderless"
      :icon="ILxcEdit"
      :title="
        disableUpdate
          ? t('device.operation.update.tooltip.disabled')
          : t('device.operation.update.tooltip.enabled')
      "
      class="invisible"
      @click="edit"
    />

    <lxc-button
      :disabled="disableUpdate"
      html-type="button"
      type="borderless"
      :icon="ILxcStopCircle"
      :title="
        disableUpdate
          ? t('device.operation.cancel.tooltip.disabled')
          : t('device.operation.cancel.tooltip.enabled')
      "
      class="invisible"
      @click="toggleConfirmCancel"
    />
  </div>

  <confirm-modal
    :is-dialog-visible="confirmCancelOperationDialogVisible"
    :title="t('device.operation.cancel.confirm.title')"
    :description="t('device.operation.cancel.confirm.description')"
    :is-loading="isCancelling"
    @cancel="toggleConfirmCancel"
    @confirm="cancel"
  />
</template>

<style lang="scss" scoped>
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
